import React, { useEffect, useRef, useState } from 'react'
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  IconButton,
  TextField,
  Tooltip,
  Typography
} from '@material-ui/core'
import Autocomplete from '@material-ui/lab/Autocomplete'
import { makeStyles } from '@material-ui/core/styles'
import AddIcon from '@material-ui/icons/Add'
import DeleteIcon from '@material-ui/icons/Delete'
import axios from 'axios'
import {
  formatMaterialLineLabel,
  formatJobsiteLabel,
  formatMoney,
  getJobsiteCoords,
  loadPlanLabel,
  scenarioLabel,
  CATEGORY_TO_AGGREGATE_TYPES
} from './offeringHelpers'

const useStyles = makeStyles((theme) => ({
  dialogTitle: {
    fontFamily: 'var(--font-ui)',
    fontSize: 18,
    fontWeight: 700,
    color: 'var(--ink)',
    padding: '20px 24px 8px'
  },
  dialogSubtitle: {
    fontFamily: 'var(--font-ui)',
    fontSize: 13,
    color: 'var(--ink-3)',
    padding: '0 24px 16px'
  },
  inputSection: {
    background: 'var(--bg)',
    border: '1px solid var(--line)',
    borderRadius: 'var(--r)',
    padding: 16,
    marginBottom: 16
  },
  inputSectionLabel: {
    fontFamily: 'var(--font-mono)',
    fontSize: 10.5,
    fontWeight: 600,
    textTransform: 'uppercase',
    letterSpacing: '0.1em',
    color: 'var(--ink-4)',
    marginBottom: 10
  },
  quickCalcControls: {
    display: 'grid',
    gridTemplateColumns: '1.2fr 1fr auto',
    gap: 10,
    alignItems: 'center',
    [theme.breakpoints.down('sm')]: {
      gridTemplateColumns: '1fr'
    }
  },
  addBtn: {
    fontFamily: 'var(--font-ui)',
    fontWeight: 600,
    fontSize: 13,
    textTransform: 'none',
    borderRadius: 'var(--r-sm)',
    padding: '6px 16px',
    minWidth: 0
  },
  quickItemRow: {
    display: 'grid',
    gridTemplateColumns: '1fr 90px 36px',
    gap: 10,
    alignItems: 'center',
    padding: '10px 14px',
    borderBottom: '1px solid var(--line)',
    '&:last-child': {
      borderBottom: 0
    }
  },
  quickItemLabel: {
    fontFamily: 'var(--font-ui)',
    fontSize: 13,
    fontWeight: 600,
    color: 'var(--ink)'
  },
  itemsList: {
    border: '1px solid var(--line)',
    borderRadius: 'var(--r-sm)',
    background: 'var(--surface)',
    overflow: 'hidden',
    marginTop: 10
  },
  quickResultGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(210px, 1fr))',
    gap: theme.spacing(1.25),
    marginTop: theme.spacing(2)
  },
  quickResultCard: {
    border: '1px solid var(--line)',
    borderRadius: 'var(--r)',
    padding: theme.spacing(1.25),
    background: 'var(--surface)'
  },
  quickResultTotal: {
    fontFamily: 'var(--font-mono)',
    fontWeight: 700,
    fontSize: '1.1rem',
    marginTop: theme.spacing(0.5),
    fontVariantNumeric: 'tabular-nums'
  },
  quickDetailRow: {
    padding: theme.spacing(1, 0),
    borderTop: `1px solid ${theme.palette.divider}`
  },
  mutedSmall: {
    fontSize: '0.76rem',
    color: theme.palette.text.secondary
  },
  chipUnknown: { backgroundColor: '#f5f5f5', color: '#757575', fontWeight: 600, fontSize: '0.68rem', height: 20 },
  // ── Supplier Comparison Table ───────────────────────────────────────────────
  supplierSection: {
    marginTop: theme.spacing(2.5),
    border: '1px solid var(--line)',
    borderRadius: 'var(--r-lg)',
    background: 'var(--surface)',
    overflow: 'hidden'
  },
  supplierSectionHeader: {
    padding: '10px 16px',
    borderBottom: '1px solid var(--line)',
    background: 'var(--bg)',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center'
  },
  supplierSectionTitle: {
    fontFamily: 'var(--font-ui)',
    fontSize: 14,
    fontWeight: 700,
    color: 'var(--ink)'
  },
  supplierSectionMeta: {
    fontFamily: 'var(--font-mono)',
    fontSize: 11,
    color: 'var(--ink-4)'
  },
  supplierGridHeader: {
    display: 'grid',
    padding: '6px 16px',
    background: 'var(--bg)',
    borderBottom: '2px solid var(--line)',
    alignItems: 'center',
    gap: 8
  },
  supplierGridHeaderCell: {
    fontFamily: 'var(--font-mono)',
    fontSize: '10.5px',
    fontWeight: 600,
    textTransform: 'uppercase',
    letterSpacing: '0.1em',
    color: 'var(--ink-4)'
  },
  supplierRow: {
    display: 'grid',
    padding: '10px 16px',
    borderBottom: '1px solid var(--line)',
    alignItems: 'center',
    gap: 8,
    transition: 'background 0.12s',
    '&:last-child': { borderBottom: 0 },
    '&:hover': { background: 'var(--bg)' }
  },
  supplierName: {
    fontFamily: 'var(--font-ui)',
    fontSize: 13,
    fontWeight: 600,
    color: 'var(--ink)'
  },
  supplierLocation: {
    fontFamily: 'var(--font-ui)',
    fontSize: 11,
    color: 'var(--ink-3)',
    lineHeight: 1.2
  },
  priceCell: {
    fontFamily: 'var(--font-mono)',
    fontSize: 13,
    fontWeight: 600,
    fontVariantNumeric: 'tabular-nums',
    color: 'var(--ink)'
  },
  bestPrice: {
    color: 'var(--material)',
    background: 'var(--material-soft)',
    borderRadius: 4,
    padding: '2px 6px',
    display: 'inline-block'
  },
  noPrice: {
    fontFamily: 'var(--font-mono)',
    fontSize: 12,
    color: 'var(--ink-4)'
  },
  requestPricingBtn: {
    fontFamily: 'var(--font-mono)',
    fontSize: 10.5,
    fontWeight: 600,
    color: 'var(--accent)',
    background: 'var(--accent-soft)',
    border: '1px solid var(--accent)',
    borderRadius: 4,
    padding: '2px 8px',
    cursor: 'pointer',
    textTransform: 'uppercase',
    letterSpacing: '0.04em',
    transition: 'background 0.12s',
    '&:hover': { background: '#f0d5c5' }
  },
  transportDetail: {
    fontFamily: 'var(--font-mono)',
    fontSize: 11,
    color: 'var(--ink-3)',
    lineHeight: 1.4
  },
  rfpBar: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 12,
    padding: '10px 16px',
    background: 'var(--accent-soft)',
    borderBottom: '1px solid var(--line)',
    borderRadius: 'var(--r) var(--r) 0 0'
  },
  rfpBarText: {
    fontFamily: 'var(--font-ui)',
    fontSize: 13,
    fontWeight: 600,
    color: 'var(--accent-2)'
  },
  rfpCheckbox: {
    width: 16,
    height: 16,
    accentColor: 'var(--accent)',
    cursor: 'pointer',
    marginRight: 8
  },
  supplierRowSelectable: {
    cursor: 'pointer',
    '&:hover': { background: 'var(--accent-soft) !important' }
  },
  errorText: {
    color: '#c62828',
    fontSize: '0.8rem',
    marginTop: theme.spacing(0.5)
  }
}))

export default function QuickCalculator ({ open, onClose, prefillMaterial, prefillGradation }) {
  const classes = useStyles()

  // ── Jobsite state ────────────────────────────────────────────────────────────
  const [jobsiteInput, setJobsiteInput] = useState('')
  const [jobsiteOptions, setJobsiteOptions] = useState([])
  const [jobsiteLoading, setJobsiteLoading] = useState(false)
  const [selectedJobsite, setSelectedJobsite] = useState(null)
  const jobsiteDebounceRef = useRef(null)

  // ── Material + gradation search state ────────────────────────────────────────
  const [quickMaterialInput, setQuickMaterialInput] = useState('')
  const [quickMaterialOptions, setQuickMaterialOptions] = useState([])
  const [quickMaterialLoading, setQuickMaterialLoading] = useState(false)
  const [selectedQuickMaterial, setSelectedQuickMaterial] = useState(null)
  const quickMaterialDebounceRef = useRef(null)
  const [quickGradationOptions, setQuickGradationOptions] = useState([])
  const [quickGradationLoading, setQuickGradationLoading] = useState(false)
  const [selectedQuickGradation, setSelectedQuickGradation] = useState(null)

  // ── Transport popover state ────────────────────────────────────────────────

  // ── Items + results ──────────────────────────────────────────────────────────
  const [quickItems, setQuickItems] = useState([])
  const [quickCalcLoading, setQuickCalcLoading] = useState(false)
  const [quickCalcError, setQuickCalcError] = useState('')
  const [quickCalcResult, setQuickCalcResult] = useState(null)
  const [rfpSelecting, setRfpSelecting] = useState(false)
  const [rfpSelected, setRfpSelected] = useState({})

  // ── Jobsite debounced search ─────────────────────────────────────────────────
  useEffect(() => {
    if (!open) return undefined
    if (jobsiteDebounceRef.current) clearTimeout(jobsiteDebounceRef.current)

    if (!jobsiteInput || jobsiteInput.length < 2) {
      setJobsiteOptions([])
      return undefined
    }

    setJobsiteLoading(true)
    jobsiteDebounceRef.current = setTimeout(() => {
      axios.get('/api/customer/jobsites/search', { params: { q: jobsiteInput } })
        .then((res) => {
          const data = res.data && Array.isArray(res.data.jobsites) ? res.data.jobsites : []
          setJobsiteOptions(data.filter(site => Boolean(getJobsiteCoords(site))))
        })
        .catch((err) => console.error('[QuickCalculator] jobsite search error:', err))
        .finally(() => setJobsiteLoading(false))
    }, 300)

    return () => { if (jobsiteDebounceRef.current) clearTimeout(jobsiteDebounceRef.current) }
  }, [open, jobsiteInput])

  // ── Material debounced search ────────────────────────────────────────────────
  useEffect(() => {
    if (!open) return undefined
    if (quickMaterialDebounceRef.current) clearTimeout(quickMaterialDebounceRef.current)

    if (!quickMaterialInput || quickMaterialInput.length < 2) {
      setQuickMaterialOptions([])
      return undefined
    }

    setQuickMaterialLoading(true)
    quickMaterialDebounceRef.current = setTimeout(() => {
      axios.get('/api/aggregate-dashboard/materials', {
        params: {
          search: quickMaterialInput.trim(),
          limit: 20
        }
      })
        .then((res) => {
          const data = Array.isArray(res.data) ? res.data : (res.data.materials || [])
          setQuickMaterialOptions(data)
        })
        .catch((err) => console.error('[QuickCalculator] material search error:', err))
        .finally(() => setQuickMaterialLoading(false))
    }, 300)

    return () => { if (quickMaterialDebounceRef.current) clearTimeout(quickMaterialDebounceRef.current) }
  }, [open, quickMaterialInput])

  // ── Gradation fetch when material is selected ──────────────────────────────
  useEffect(() => {
    if (!open || !selectedQuickMaterial) {
      setQuickGradationOptions([])
      return
    }
    setQuickGradationLoading(true)
    const category = selectedQuickMaterial.materialCategory
    const allowedTypes = category ? CATEGORY_TO_AGGREGATE_TYPES[category] : null
    axios.get('/api/aggregate-dashboard/gradations')
      .then((res) => {
        const all = Array.isArray(res.data) ? res.data : []
        setQuickGradationOptions(allowedTypes ? all.filter((g) => allowedTypes.includes(g.aggregateType)) : all)
      })
      .catch((err) => console.error('[QuickCalculator] gradation fetch error:', err))
      .finally(() => setQuickGradationLoading(false))
  }, [open, selectedQuickMaterial])

  // ── Handlers ─────────────────────────────────────────────────────────────────
  const handleClose = () => {
    if (quickCalcLoading) return
    setQuickCalcError('')
    onClose()
  }

  const handleAddQuickItem = () => {
    if (!selectedQuickMaterial || !selectedQuickGradation) return
    setQuickItems((items) => {
      const compositeKey = String(selectedQuickMaterial._id) + String(selectedQuickGradation._id)
      if (items.some(item => String(item.material._id) + String(item.gradation._id) === compositeKey)) return items
      return [...items, { material: selectedQuickMaterial, gradation: selectedQuickGradation, quantity: 1 }]
    })
    setSelectedQuickMaterial(null)
    setSelectedQuickGradation(null)
    setQuickMaterialInput('')
    setQuickMaterialOptions([])
    setQuickGradationOptions([])
    setQuickCalcResult(null)
    setQuickCalcError('')
  }

  const handleQuickQtyChange = (key, value) => {
    setQuickItems((items) => items.map((item) => (
      `${item.material._id}:${item.gradation._id}` === key
        ? { ...item, quantity: value }
        : item
    )))
    setQuickCalcResult(null)
  }

  const handleRemoveQuickItem = (key) => {
    setQuickItems((items) => items.filter(item => `${item.material._id}:${item.gradation._id}` !== key))
    setQuickCalcResult(null)
  }

  const handleRunQuickCalc = async () => {
    const coords = getJobsiteCoords(selectedJobsite)
    if (!coords) { setQuickCalcError('Select a jobsite with coordinates'); return }
    if (quickItems.length === 0) { setQuickCalcError('Add at least one material and gradation'); return }

    const items = quickItems.map(item => ({
      materialProfileId: item.material._id,
      gradationId: item.gradation._id,
      quantity: Number(item.quantity) || 0
    }))
    if (items.some(item => !item.quantity || item.quantity <= 0)) {
      setQuickCalcError('Each item needs a quantity greater than zero')
      return
    }

    setQuickCalcLoading(true)
    setQuickCalcError('')
    try {
      const res = await axios.post('/api/aggregate-dashboard/fulfillment-calc/quick', {
        jobsiteId: selectedJobsite._id,
        jobsiteLat: coords.lat,
        jobsiteLng: coords.lng,
        items
      })
      setQuickCalcResult(res.data)
    } catch (err) {
      const msg = err.response && err.response.data && err.response.data.error
        ? err.response.data.error
        : err.message
      setQuickCalcError(msg)
    } finally {
      setQuickCalcLoading(false)
    }
  }

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <div className={classes.dialogTitle}>Aggregate Quick Calculator</div>
      <div className={classes.dialogSubtitle}>Compare supplier pricing for materials at a specific jobsite</div>
      <DialogContent>
        {/* ── Jobsite ──────────────────────────────────────────── */}
        <div className={classes.inputSection}>
          <div className={classes.inputSectionLabel}>Jobsite</div>
          <Autocomplete
            value={selectedJobsite}
            onChange={(e, newValue) => {
              setSelectedJobsite(newValue)
              setQuickCalcResult(null)
              setQuickCalcError('')
            }}
            inputValue={jobsiteInput}
            onInputChange={(e, newInput) => setJobsiteInput(newInput)}
            options={jobsiteOptions}
            loading={jobsiteLoading}
            getOptionLabel={formatJobsiteLabel}
            getOptionSelected={(opt, val) => opt._id === val._id}
            openOnFocus
            autoHighlight
            selectOnFocus
            handleHomeEndKeys
            forcePopupIcon={false}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Jobsite"
                variant="outlined"
                size="small"
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {jobsiteLoading ? <CircularProgress color="inherit" size={18} /> : null}
                      {params.InputProps.endAdornment}
                    </>
                  )
                }}
              />
            )}
            noOptionsText={jobsiteInput.length < 2 ? 'Type to search...' : 'No jobsites with coordinates'}
          />
        </div>

        {/* ── Add Materials ────────────────────────────────────── */}
        <div className={classes.inputSection}>
          <div className={classes.inputSectionLabel}>Add Materials</div>
          <div className={classes.quickCalcControls}>
          <Autocomplete
            value={selectedQuickMaterial}
            onChange={(e, newValue) => {
              setSelectedQuickMaterial(newValue)
              setSelectedQuickGradation(null)
              setQuickCalcError('')
            }}
            inputValue={quickMaterialInput}
            onInputChange={(e, newInput) => setQuickMaterialInput(newInput)}
            options={quickMaterialOptions}
            loading={quickMaterialLoading}
            getOptionLabel={(opt) => opt.displayName || ''}
            getOptionSelected={(opt, val) => opt._id === val._id}
            openOnFocus
            autoHighlight
            selectOnFocus
            handleHomeEndKeys
            forcePopupIcon={false}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Material"
                variant="outlined"
                size="small"
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {quickMaterialLoading ? <CircularProgress color="inherit" size={18} /> : null}
                      {params.InputProps.endAdornment}
                    </>
                  )
                }}
              />
            )}
            noOptionsText={quickMaterialInput.length < 2 ? 'Type to search...' : 'No materials'}
          />

          <Autocomplete
            value={selectedQuickGradation}
            onChange={(e, newValue) => {
              setSelectedQuickGradation(newValue)
              setQuickCalcError('')
            }}
            options={quickGradationOptions}
            loading={quickGradationLoading}
            disabled={!selectedQuickMaterial}
            getOptionLabel={(opt) => opt.displayName || ''}
            getOptionSelected={(opt, val) => opt._id === val._id}
            openOnFocus
            autoHighlight
            selectOnFocus
            handleHomeEndKeys
            forcePopupIcon={false}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Gradation"
                variant="outlined"
                size="small"
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {quickGradationLoading ? <CircularProgress color="inherit" size={18} /> : null}
                      {params.InputProps.endAdornment}
                    </>
                  )
                }}
              />
            )}
            noOptionsText={selectedQuickMaterial ? 'No gradations' : 'Select material first'}
          />

          <Button
            variant="outlined"
            color="primary"
            className={classes.addBtn}
            startIcon={<AddIcon />}
            onClick={handleAddQuickItem}
            disabled={!selectedQuickMaterial || !selectedQuickGradation}
          >
            Add
          </Button>
          </div>

          {quickItems.length > 0 && (
            <div className={classes.itemsList}>
              {quickItems.map((item) => (
                <div key={`${item.material._id}:${item.gradation._id}`} className={classes.quickItemRow}>
                  <span className={classes.quickItemLabel}>
                    {formatMaterialLineLabel(item)}
                  </span>
                  <TextField
                    label="Tons"
                    type="number"
                    value={item.quantity}
                    onChange={(e) => handleQuickQtyChange(`${item.material._id}:${item.gradation._id}`, e.target.value)}
                    variant="outlined"
                    size="small"
                    inputProps={{ min: 0, step: 0.25 }}
                  />
                  <Tooltip title="Remove">
                    <IconButton size="small" onClick={() => handleRemoveQuickItem(`${item.material._id}:${item.gradation._id}`)}>
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </div>
              ))}
            </div>
          )}
        </div>

        {quickCalcError && (
          <Typography className={classes.errorText}>{quickCalcError}</Typography>
        )}

        {quickCalcResult && Array.isArray(quickCalcResult.summaries) && quickCalcResult.summaries.length > 0 && (
          <>
            {/* ── Best Price Summary Cards ─────────────────────────── */}
            <div className={classes.quickResultGrid}>
              {quickCalcResult.summaries.map((summary) => (
                <div key={summary.category} className={classes.quickResultCard}>
                  <Box display="flex" alignItems="center" justifyContent="space-between">
                    <Typography variant="subtitle2">{summary.label}</Typography>
                    {!summary.complete && (
                      <Chip size="small" label={`${summary.missingLineCount} missing`} className={classes.chipUnknown} />
                    )}
                  </Box>
                  <Typography className={classes.quickResultTotal}>
                    {summary.transportCostIncomplete
                      ? `Material ${formatMoney(summary.materialCost)}`
                      : formatMoney(summary.totalCost)}
                  </Typography>
                  <Typography className={classes.mutedSmall}>
                    {summary.transportCostIncomplete
                      ? 'Transport cost unavailable'
                      : `Materials ${formatMoney(summary.materialCost)} + transport ${formatMoney(summary.transportCost)}`}
                  </Typography>
                </div>
              ))}
            </div>

            {/* ── Supplier Comparison per Material Line ─────────────── */}
            {(quickCalcResult.lines || []).map((line) => {
              // Build supplier rows from all options across all scenarios
              const bySupplier = {}
              for (const option of (line.options || [])) {
                const key = `${option.supplier}::${option.location}`
                if (!bySupplier[key]) {
                  bySupplier[key] = { supplier: option.supplier, location: option.location, scenarios: {}, hasPricedOptions: true }
                }
                const existing = bySupplier[key].scenarios[option.category]
                if (!existing || option.totalCost < existing.totalCost) {
                  bySupplier[key].scenarios[option.category] = option
                }
              }
              // Also include unavailable delivery entries
              for (const ua of (quickCalcResult.unavailableOptions || [])) {
                if (ua.lineId !== line.lineId || ua.category !== 'DELIVERY') continue
                const key = `${ua.supplier}::${ua.location}`
                if (!bySupplier[key]) {
                  bySupplier[key] = { supplier: ua.supplier, location: ua.location, scenarios: {}, hasPricedOptions: false }
                }
                if (!bySupplier[key].scenarios.DELIVERY) {
                  bySupplier[key].scenarios.DELIVERY = { unavailable: true, reason: ua.reason, maxDeliveryMiles: ua.maxDeliveryMiles, routeDistanceMiles: ua.routeDistanceMiles }
                }
              }
              // Merge in allSuppliers — suppliers who carry this material color/type (even without pricing)
              for (const s of (quickCalcResult.allSuppliers || [])) {
                const key = `${s.vendorName}::${s.locationName || ''}`
                if (!bySupplier[key]) {
                  bySupplier[key] = {
                    supplier: s.vendorName,
                    location: s.locationName,
                    scenarios: {},
                    hasPricedOptions: false,
                    vendorId: s.vendorId,
                    carriesMaterial: s.materialName,
                    isExactMatch: s.isExactMatch
                  }
                }
              }
              const supplierRows = Object.values(bySupplier)

              // Determine which scenario columns have data
              const scenarioCols = ['PIT_PICKUP', 'YARD_PICKUP', 'DELIVERY', 'PICKUP'].filter(cat =>
                supplierRows.some(r => r.scenarios[cat])
              )
              const colLabels = { PIT_PICKUP: 'Pit Pickup', YARD_PICKUP: 'Yard Pick-Up', DELIVERY: 'Delivery', PICKUP: 'Pickup' }

              // Find best (cheapest) per column
              const bestByCol = {}
              for (const col of scenarioCols) {
                let best = null
                for (const row of supplierRows) {
                  const s = row.scenarios[col]
                  if (s && !s.unavailable && s.totalCost > 0) {
                    if (!best || s.totalCost < best) best = s.totalCost
                  }
                }
                bestByCol[col] = best
              }

              const gridCols = `minmax(140px, 1.5fr) ${scenarioCols.map(() => 'minmax(110px, 1fr)').join(' ')}`
              const materialLabel = line.offering
                ? `${line.offering.material}${line.offering.gradation ? ` / ${line.offering.gradation}` : ''}`
                : 'Material'

              const toggleRfpSupplier = (supplierKey) => {
                setRfpSelected(prev => ({ ...prev, [supplierKey]: !prev[supplierKey] }))
              }
              const rfpCount = Object.values(rfpSelected).filter(Boolean).length

              return (
                <div key={line.lineId} className={classes.supplierSection}>
                  <div className={classes.supplierSectionHeader}>
                    <span className={classes.supplierSectionTitle}>{materialLabel}</span>
                    <Box display="flex" alignItems="center" gap={1}>
                      <span className={classes.supplierSectionMeta}>
                        {line.quantity} ton{line.quantity !== 1 ? 's' : ''} · {supplierRows.length} supplier{supplierRows.length !== 1 ? 's' : ''}
                      </span>
                      {!rfpSelecting ? (
                        <button type="button" className={classes.requestPricingBtn} onClick={() => setRfpSelecting(true)}>
                          Request Pricing
                        </button>
                      ) : (
                        <Box display="flex" alignItems="center" gap={1}>
                          <button type="button" className={classes.requestPricingBtn}
                            style={{ opacity: rfpCount === 0 ? 0.5 : 1 }}
                            disabled={rfpCount === 0}
                            onClick={() => {
                              const selected = supplierRows.filter((_, i) => rfpSelected[`${_.supplier}::${_.location}::${i}`])
                              console.log('[RFP] Send pricing request to:', selected.map(s => s.supplier))
                              // TODO: open RFP dialog / trigger email
                              setRfpSelecting(false)
                              setRfpSelected({})
                            }}
                          >
                            Send to {rfpCount} supplier{rfpCount !== 1 ? 's' : ''}
                          </button>
                          <button type="button" className={classes.requestPricingBtn}
                            style={{ background: 'transparent', border: '1px solid var(--ink-4)', color: 'var(--ink-3)' }}
                            onClick={() => { setRfpSelecting(false); setRfpSelected({}) }}
                          >
                            Cancel
                          </button>
                        </Box>
                      )}
                    </Box>
                  </div>

                  {/* Grid header */}
                  <div className={classes.supplierGridHeader} style={{ gridTemplateColumns: rfpSelecting ? `28px ${gridCols}` : gridCols }}>
                    {rfpSelecting && <span />}
                    <span className={classes.supplierGridHeaderCell}>SUPPLIER</span>
                    {scenarioCols.map(col => (
                      <span key={col} className={classes.supplierGridHeaderCell}>{colLabels[col] || col}</span>
                    ))}
                  </div>

                  {/* Supplier rows */}
                  {supplierRows.map((row, idx) => {
                    const rowKey = `${row.supplier}::${row.location}::${idx}`
                    return (
                    <div
                      key={rowKey}
                      className={`${classes.supplierRow} ${rfpSelecting ? classes.supplierRowSelectable : ''}`}
                      style={{ gridTemplateColumns: rfpSelecting ? `28px ${gridCols}` : gridCols }}
                      onClick={rfpSelecting ? () => toggleRfpSupplier(rowKey) : undefined}
                    >
                      {rfpSelecting && (
                        <input
                          type="checkbox"
                          className={classes.rfpCheckbox}
                          checked={!!rfpSelected[rowKey]}
                          onChange={() => toggleRfpSupplier(rowKey)}
                          onClick={(e) => e.stopPropagation()}
                        />
                      )}
                      <div>
                        <div className={classes.supplierName}>{row.supplier}</div>
                        {row.location && <div className={classes.supplierLocation}>{row.location}</div>}
                        {row.carriesMaterial && !row.isExactMatch && (
                          <div className={classes.supplierLocation} style={{ fontStyle: 'italic' }}>
                            Carries: {row.carriesMaterial}
                          </div>
                        )}
                      </div>
                      {scenarioCols.map(col => {
                        const s = row.scenarios[col]
                        if (!s) {
                          return (
                            <div key={col}>
                              <button type="button" className={classes.requestPricingBtn}>Request Price</button>
                            </div>
                          )
                        }
                        if (s.unavailable) {
                          return (
                            <Tooltip key={col} title={s.reason || 'Delivery unavailable'}>
                              <div>
                                <span className={classes.noPrice}>Out of range</span>
                                {s.routeDistanceMiles && (
                                  <div className={classes.transportDetail}>{s.routeDistanceMiles} mi</div>
                                )}
                              </div>
                            </Tooltip>
                          )
                        }
                        const isBest = bestByCol[col] != null && s.totalCost === bestByCol[col]
                        return (
                          <Tooltip key={col} title={
                            `${formatMoney(s.pricePerTon)}/ton · ${scenarioLabel(s)}` +
                            (loadPlanLabel(s) ? ` · ${loadPlanLabel(s)}` : '') +
                            (s.routeDistanceMiles ? ` · ${s.routeDistanceMiles} mi` : '') +
                            (s.routeEstimated ? ' (est)' : '')
                          }>
                            <div>
                              <span className={`${classes.priceCell} ${isBest ? classes.bestPrice : ''}`}>
                                {formatMoney(s.totalCost)}
                              </span>
                              <div className={classes.transportDetail}>
                                {formatMoney(s.materialCost)} mat + {formatMoney(s.transportCost)} transport
                              </div>
                            </div>
                          </Tooltip>
                        )
                      })}
                    </div>
                  )
                  })}
                </div>
              )
            })}
          </>
        )}

        {quickCalcResult && (!quickCalcResult.summaries || quickCalcResult.summaries.length === 0) && (
          <Typography variant="body2" color="textSecondary" style={{ marginTop: 16 }}>
            No pricing options available for the selected jobsite, material, and gradation.
          </Typography>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={quickCalcLoading}>Close</Button>
        <Button
          onClick={handleRunQuickCalc}
          color="primary"
          variant="contained"
          disabled={quickCalcLoading || !selectedJobsite || quickItems.length === 0}
        >
          {quickCalcLoading ? <CircularProgress size={18} /> : 'Calculate'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}
